import { supabase } from "@/lib/supabase";
import type { Tables } from "@synkro/db";

export type ShippingRate = Tables<"shipping_rates">;

export interface RateDraft {
  district: string;
  province: string;
  department: string;
  price: string;
  free_from: string;
  estimated_minutes: string;
  active: boolean;
}

export const VACIA: RateDraft = {
  district: "",
  province: "Lima",
  department: "Lima",
  price: "",
  free_from: "",
  estimated_minutes: "",
  active: true,
};

export async function fetchRates(organizationId: string): Promise<ShippingRate[]> {
  const { data, error } = await supabase
    .from("shipping_rates")
    .select("*")
    .eq("organization_id", organizationId)
    .order("district");

  if (error) throw error;
  return data ?? [];
}

const numero = (v: string): number | null => {
  const limpio = v.trim().replace(",", ".");
  if (limpio === "") return null;
  const n = Number(limpio);
  return Number.isFinite(n) ? n : null;
};

/**
 * El dinero va y viene como texto.
 *
 * `numeric(12,2)` no cabe en el `number` de JavaScript sin perder precision,
 * asi que los tipos generados lo declaran `string` y conviene no discutirlo:
 * se manda la cadena y PostgreSQL hace la conversion exacta.
 */
const dinero = (v: string): string | null => {
  const n = numero(v);
  return n === null ? null : n.toFixed(2);
};

function aFila(organizationId: string, borrador: RateDraft) {
  return {
    organization_id: organizationId,
    district: borrador.district.trim(),
    province: borrador.province.trim() || "Lima",
    department: borrador.department.trim() || "Lima",
    price: dinero(borrador.price) ?? "0.00",
    free_from: dinero(borrador.free_from),
    estimated_minutes: numero(borrador.estimated_minutes),
    active: borrador.active,
  };
}

export async function createRate(organizationId: string, borrador: RateDraft) {
  const { error } = await supabase.from("shipping_rates").insert(aFila(organizationId, borrador));
  if (error) throw error;
}

export async function updateRate(id: string, organizationId: string, borrador: RateDraft) {
  const { organization_id: _omitido, ...cambios } = aFila(organizationId, borrador);
  const { error } = await supabase.from("shipping_rates").update(cambios).eq("id", id);
  if (error) throw error;
}

/**
 * Se desactiva, no se borra.
 *
 * Un pedido viejo guarda el distrito al que se envio; borrar la zona no lo
 * rompe, pero si alguien la recrea con otro precio la historia deja de
 * cuadrar. Desactivar la saca del checkout y conserva el rastro.
 */
export async function toggleRate(id: string, active: boolean) {
  const { error } = await supabase.from("shipping_rates").update({ active }).eq("id", id);
  if (error) throw error;
}

export async function deleteRate(id: string) {
  const { error } = await supabase.from("shipping_rates").delete().eq("id", id);
  if (error) throw error;
}

export function aBorrador(fila: ShippingRate): RateDraft {
  return {
    district: fila.district ?? "",
    province: fila.province ?? "Lima",
    department: fila.department ?? "Lima",
    price: fila.price === null ? "" : String(fila.price),
    free_from: fila.free_from === null ? "" : String(fila.free_from),
    estimated_minutes:
      fila.estimated_minutes === null ? "" : String(fila.estimated_minutes),
    active: Boolean(fila.active),
  };
}

export function validar(borrador: RateDraft): Record<string, string> {
  const errores: Record<string, string> = {};

  if (!borrador.district.trim()) errores.district = "Escribe el distrito.";

  const precio = numero(borrador.price);
  if (precio === null) errores.price = "Indica el precio. Usa 0 si es gratis.";
  else if (precio < 0) errores.price = "El precio no puede ser negativo.";

  const desde = numero(borrador.free_from);
  if (borrador.free_from.trim() !== "" && (desde === null || desde <= 0)) {
    errores.free_from = "Deja el campo vacío si no hay envío gratis.";
  }

  const minutos = numero(borrador.estimated_minutes);
  if (borrador.estimated_minutes.trim() !== "" && (minutos === null || minutos <= 0)) {
    errores.estimated_minutes = "Indica los minutos como número entero.";
  }

  return errores;
}
